import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, Trash2, Mail, Search } from 'lucide-react';
import { api } from '../../lib/api';
import { Pagination } from '../../components/admin/Pagination';
import { SegmentedTabs } from '../../components/admin/SegmentedTabs';
import { SortableTh, TableSkeleton } from '../../components/admin/SortableTable';
import { TableEmpty } from '../../components/admin/TableEmpty';
import { useDebounce } from '../../hooks/useDebounce';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const FILTERS = [
    { key: 'new', label: 'New' },
    { key: 'archived', label: 'Archived' },
    { key: 'all', label: 'All' },
];

export default function AdminMessages() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState('new');
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState({ by: 'created_at', dir: 'desc' });
    const [selected, setSelected] = useState(null);
    const PAGE_SIZE = 10;
    const debouncedSearch = useDebounce(search);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: PAGE_SIZE, sort_by: sort.by, sort_dir: sort.dir };
            if (filter !== 'all') params.status = filter;
            if (debouncedSearch) params.search = debouncedSearch;
            const r = await api.get('/admin/contact-messages', { params });
            setItems(r.data.items);
            setTotal(r.data.total);
        } catch {
            toast.error('Failed to load messages.');
        } finally {
            setLoading(false);
        }
    }, [filter, page, debouncedSearch, sort]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [filter, debouncedSearch, sort]);

    const setStatus = async (id, status) => {
        try {
            await api.put(`/admin/contact-messages/${id}`, { status });
            toast.success(status === 'archived' ? 'Message archived.' : 'Message restored.');
            setSelected((s) => (s && s.id === id ? { ...s, status } : s));
            await load();
        } catch { toast.error('Action failed.'); }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/admin/contact-messages/${id}`);
            toast.success('Message deleted.');
            setSelected((s) => (s && s.id === id ? null : s));
            await load();
        } catch { toast.error('Delete failed.'); }
    };

    const StatusBadge = ({ status }) => (
        <span
            className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full"
            style={status === 'archived'
                ? { backgroundColor: 'rgba(90,36,50,0.10)', color: 'var(--ink-muted)' }
                : { backgroundColor: 'var(--brand-burgundy)', color: '#fff' }}
        >
            {status}
        </span>
    );

    return (
        <div data-testid="admin-messages-page">
            <div className="mb-8">
                <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>Atelier</p>
                <h1 className="font-serif text-3xl sm:text-4xl mt-1" style={{ color: 'var(--brand-burgundy)' }}>Messages</h1>
                <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>Contact form submissions — click a row to read the full message.</p>
            </div>

            <div className="mb-6">
                <SegmentedTabs value={filter} onChange={setFilter} options={FILTERS} testIdPrefix="admin-messages-filter" />
            </div>

            <div className="mb-4 flex items-center justify-between gap-4">
                <div className="relative max-w-sm w-full">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-muted)' }} />
                    <input
                        data-testid="admin-messages-search-input"
                        type="text"
                        placeholder="Search by name, email, subject, message..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="input-luxury pl-9"
                    />
                </div>
                <p className="text-xs whitespace-nowrap hidden sm:block" style={{ color: 'var(--ink-muted)' }} data-testid="admin-messages-count">{total} message{total === 1 ? '' : 's'}</p>
            </div>

            <div className="luxury-card overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]" data-testid="admin-messages-table">
                    <thead className="bg-[var(--brand-rose)]/40">
                        <tr className="text-left">
                            <SortableTh label="Name" field="name" sort={sort} onSort={setSort} />
                            <SortableTh label="Email" field="email" sort={sort} onSort={setSort} />
                            <SortableTh label="Subject" field="subject" sort={sort} onSort={setSort} />
                            <SortableTh label="Status" field="status" sort={sort} onSort={setSort} />
                            <SortableTh label="Received" field="created_at" sort={sort} onSort={setSort} />
                            <th className="p-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    {loading ? <TableSkeleton cols={6} /> : (
                    <tbody>
                        {items.map((m) => (
                            <tr key={m.id} onClick={() => setSelected(m)} className="border-t border-[rgba(90,36,50,0.08)] cursor-pointer hover:bg-[var(--brand-rose)]/20 transition-colors" data-testid={`admin-message-row-${m.id}`}>
                                <td className="p-4 font-serif text-base" style={{ color: 'var(--brand-burgundy)' }}>{m.name}</td>
                                <td className="p-4 text-xs" style={{ color: 'var(--ink-muted)' }}>{m.email}</td>
                                <td className="p-4 max-w-[220px] truncate">{m.subject || <span style={{ color: 'var(--ink-muted)' }}>—</span>}</td>
                                <td className="p-4"><StatusBadge status={m.status} /></td>
                                <td className="p-4 text-xs" style={{ color: 'var(--ink-muted)' }}>{new Date(m.created_at).toLocaleString()}</td>
                                <td className="p-4 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                    {m.status === 'new' ? (
                                        <button onClick={() => setStatus(m.id, 'archived')} data-testid={`admin-message-archive-${m.id}`} className="p-2 hover:bg-[var(--brand-rose)]/50 rounded" aria-label="Archive"><Archive className="w-4 h-4" style={{ color: 'var(--brand-burgundy)' }} /></button>
                                    ) : (
                                        <button onClick={() => setStatus(m.id, 'new')} data-testid={`admin-message-restore-${m.id}`} className="p-2 hover:bg-[var(--brand-rose)]/50 rounded" aria-label="Restore"><ArchiveRestore className="w-4 h-4" style={{ color: 'var(--brand-burgundy)' }} /></button>
                                    )}
                                    <button onClick={() => remove(m.id)} data-testid={`admin-message-delete-${m.id}`} className="p-2 hover:bg-[var(--brand-rose)]/50 rounded" aria-label="Delete"><Trash2 className="w-4 h-4" style={{ color: 'var(--brand-burgundy)' }} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    )}
                </table>
                </div>
                {!loading && items.length === 0 && (
                    <TableEmpty icon={Mail} title="No messages" subtitle="Contact form submissions will appear here." testId="admin-messages-empty" />
                )}
            </div>

            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testIdPrefix="admin-messages-pagination" />

            <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white" data-testid="admin-message-detail-dialog">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>{selected.subject || 'Message'}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-serif text-lg" style={{ color: 'var(--brand-burgundy)' }}>{selected.name}</p>
                                        <a href={`mailto:${selected.email}`} className="text-xs inline-flex items-center gap-1.5 hover:underline" style={{ color: 'var(--brand-burgundy)' }}><Mail className="w-3 h-3" /> {selected.email}</a>
                                    </div>
                                    <StatusBadge status={selected.status} />
                                </div>
                                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{new Date(selected.created_at).toLocaleString()}</p>
                                <div className="rounded-lg border border-[rgba(90,36,50,0.10)] p-4 text-sm whitespace-pre-wrap" style={{ color: 'var(--ink)' }}>{selected.message}</div>
                                <div className="flex items-center gap-2 border-t border-[rgba(90,36,50,0.08)] pt-4">
                                    {selected.status === 'new' ? (
                                        <button onClick={() => setStatus(selected.id, 'archived')} className="btn-outline-burgundy px-4 py-2 rounded-lg text-xs uppercase tracking-[0.16em] inline-flex items-center gap-2"><Archive className="w-4 h-4" /> Archive</button>
                                    ) : (
                                        <button onClick={() => setStatus(selected.id, 'new')} className="btn-outline-burgundy px-4 py-2 rounded-lg text-xs uppercase tracking-[0.16em] inline-flex items-center gap-2"><ArchiveRestore className="w-4 h-4" /> Restore</button>
                                    )}
                                    <a href={`mailto:${selected.email}`} className="btn-burgundy px-4 py-2 rounded-lg text-xs uppercase tracking-[0.16em] inline-flex items-center gap-2"><Mail className="w-4 h-4" /> Reply</a>
                                    <button onClick={() => remove(selected.id)} className="p-2 rounded-lg hover:bg-[var(--brand-rose)]/50 transition-colors ml-auto" aria-label="Delete message"><Trash2 className="w-4 h-4" style={{ color: 'var(--brand-burgundy)' }} /></button>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
